import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  Lock, 
  ArrowRight, 
  ArrowLeft,
  AlertCircle,
  Loader2,
  PlayCircle,
  Copy
} from 'lucide-react';
import { uploadPDF, generateV2Summary, generateV2Script, generateVideo, generateAudio } from '../services/api';

const V2Workflow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [workflowData, setWorkflowData] = useState({
    pdf: null,
    title: '',
    summary: '',
    caseStudies: '',
    recommendations: '',
    selectedAngles: [], // Changed from selectedAngle to array
    selectedCTA: null, // New field for CTA selection
    scripts: [], // Changed from script to array
    audioTracks: [], // Changed from audioTrack to array
    videoTracks: [], // Changed from videoTrack to array
    articleId: null
  });

  const steps = [
    { id: 1, title: 'Upload PDF', icon: Upload, description: 'Upload your PDF document' },
    { id: 2, title: 'Create Summary', icon: FileText, description: 'Generate summary and angles' },
    { id: 3, title: 'Select Angles', icon: CheckCircle, description: 'Choose your story angles (1-3)' },
    { id: 4, title: 'Select CTA', icon: CheckCircle, description: 'Choose call-to-action' },
    { id: 5, title: 'Generate Scripts', icon: FileText, description: 'Create video scripts' },
    { id: 6, title: 'Generate Audio', icon: PlayCircle, description: 'Convert scripts to audio' },
    { id: 7, title: 'Generate Videos', icon: PlayCircle, description: 'Create avatar videos' }
  ];

  const isStepEnabled = (stepId) => {
    switch (stepId) {
      case 1:
        return true; // Always enabled
      case 2:
        return workflowData.pdf && workflowData.title;
      case 3:
        return workflowData.summary && workflowData.caseStudies && workflowData.recommendations;
      case 4:
        return workflowData.selectedAngles.length > 0;
      case 5:
        return workflowData.selectedCTA;
      case 6:
        return workflowData.scripts.length > 0;
      case 7:
        return workflowData.audioTracks.length > 0;
      default:
        return false;
    }
  };

  const handleStepClick = (stepId) => {
    if (isStepEnabled(stepId)) {
      setCurrentStep(stepId);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length && isStepEnabled(currentStep + 1)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1Upload workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 2:
        return <Step2Summary workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 3:
        return <Step3SelectAngles workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 4:
        return <Step4SelectCTA workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 5:
        return <Step5GenerateScripts workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 6:
        return <Step6GenerateAudio workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      case 7:
        return <Step7GenerateVideos workflowData={workflowData} setWorkflowData={setWorkflowData} />;
      default:
        return <div>Step not found</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">V2 Workflow Hub</h1>
          <p className="text-gray-600 mt-2">Step-by-step PDF to video script creation</p>
        </div>

        <div className="flex gap-8">
          {/* Left Sidebar - Steps */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Workflow Steps</h2>
              
              <div className="space-y-4">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isEnabled = isStepEnabled(step.id);
                  const isActive = currentStep === step.id;
                  const isCompleted = workflowData[`step${step.id}Completed`];

                  return (
                    <div
                      key={step.id}
                      className={`relative flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                        isActive 
                          ? 'bg-primary-50 border-2 border-primary-200' 
                          : isEnabled 
                            ? 'hover:bg-gray-50 border-2 border-transparent' 
                            : 'opacity-50 cursor-not-allowed border-2 border-transparent'
                      }`}
                      onClick={() => handleStepClick(step.id)}
                    >
                      {/* Step Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? 'bg-green-100 text-green-600'
                          : isActive
                            ? 'bg-primary-100 text-primary-600'
                            : isEnabled
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-gray-100 text-gray-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>

                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-sm font-medium ${
                            isActive ? 'text-primary-900' : isEnabled ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {step.title}
                          </h3>
                          {!isEnabled && (
                            <Lock className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${
                          isActive ? 'text-primary-700' : isEnabled ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          {step.description}
                        </p>
                      </div>

                      {/* Step Number */}
                      <div className={`text-xs font-medium px-2 py-1 rounded ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : isEnabled
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-gray-100 text-gray-400'
                      }`}>
                        {step.id}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border">
              {/* Step Header */}
              <div className="border-b px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Step {currentStep}: {steps[currentStep - 1]?.title}
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {steps[currentStep - 1]?.description}
                    </p>
                  </div>
                  
                  {/* Navigation Buttons */}
                  <div className="flex items-center space-x-3">
                    {currentStep > 1 && (
                      <button
                        onClick={prevStep}
                        className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </button>
                    )}
                    
                    {currentStep < steps.length && isStepEnabled(currentStep + 1) && (
                      <button
                        onClick={nextStep}
                        className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6">
                {renderStepContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 1: Upload PDF
const Step1Upload = ({ workflowData, setWorkflowData }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setUploadStatus(null);
      setWorkflowData(prev => ({
        ...prev,
        pdf: selectedFile,
        title: selectedFile.name.replace('.pdf', '')
      }));
    } else {
      setUploadStatus({ type: 'error', message: 'Please select a valid PDF file' });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadStatus(null);

    try {
      const result = await uploadPDF(file, workflowData.title, '');
      setUploadStatus({ type: 'success', message: 'PDF uploaded successfully!' });
      setWorkflowData(prev => ({
        ...prev,
        step1Completed: true,
        articleId: result.id
      }));
    } catch (error) {
      setUploadStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to upload PDF. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-primary-400 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {file ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">{file.name}</p>
              <p className="text-sm text-gray-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-gray-100 rounded-full">
              <Upload className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                Drop your PDF here, or{' '}
                <label className="text-primary-600 hover:text-primary-500 cursor-pointer">
                  browse
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  />
                </label>
              </p>
              <p className="text-sm text-gray-500">PDF files only, up to 10MB</p>
            </div>
          </div>
        )}
      </div>

      {/* Title Input */}
      {file && (
        <div className="space-y-2">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Article Title
          </label>
          <input
            type="text"
            id="title"
            value={workflowData.title}
            onChange={(e) => setWorkflowData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter a title for your article..."
          />
        </div>
      )}

      {/* Upload Button */}
      {file && workflowData.title && (
        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Upload PDF</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Status Messages */}
      {uploadStatus && (
        <div className={`p-4 rounded-md ${
          uploadStatus.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            {uploadStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <p className={`ml-3 text-sm ${
              uploadStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {uploadStatus.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 2: Create Summary
const Step2Summary = ({ workflowData, setWorkflowData }) => {
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);

  const handleGenerateSummary = async () => {
    if (!workflowData.articleId) {
      console.error('No articleId found in workflowData:', workflowData);
      setGenerationStatus({ 
        type: 'error', 
        message: 'No article ID found. Please upload a PDF first.' 
      });
      return;
    }

    console.log('Starting summary generation for article:', workflowData.articleId);
    setGenerating(true);
    setGenerationStatus(null);

    try {
      console.log('Calling generateV2Summary API...');
      const result = await generateV2Summary(workflowData.articleId);
      console.log('API response:', result);
      
      setWorkflowData(prev => ({
        ...prev,
        summary: result.summary,
        caseStudies: result.case_studies,
        recommendations: result.recommendations,
        step2Completed: true
      }));
      
      setGenerationStatus({ type: 'success', message: 'Summary and recommendations generated successfully!' });
    } catch (error) {
      console.error('Error generating summary:', error);
      setGenerationStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || error.message || 'Failed to generate summary. Please try again.' 
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Generate Button */}
      {!workflowData.summary && (
        <div className="text-center py-8">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Summary & Analysis</h3>
          <p className="text-gray-600 mb-6">
            This will analyze your PDF and create a comprehensive summary with case study identification and story angle recommendations.
          </p>
          <button
            onClick={handleGenerateSummary}
            disabled={generating}
            className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed mx-auto"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Generate Summary & Analysis</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Generated Content */}
      {workflowData.summary && (
        <div className="space-y-6">
          {/* Summary Section */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Article Summary
            </h3>
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {workflowData.summary}
              </p>
            </div>
          </div>

          {/* Case Studies Section */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
              Case Studies Identified
            </h3>
            <div className="prose max-w-none">
              {typeof workflowData.caseStudies === 'string' ? (
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {workflowData.caseStudies}
                </p>
              ) : Array.isArray(workflowData.caseStudies) ? (
                <div className="space-y-4">
                  {workflowData.caseStudies.map((study, index) => (
                    <div key={index} className="bg-white p-4 rounded-md border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2">
                        {String(study.title || study.name || `Case Study ${index + 1}`)}
                      </h4>
                      <p className="text-gray-700">
                        {String(study.description || study.content || study)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : workflowData.caseStudies && typeof workflowData.caseStudies === 'object' ? (
                <div className="space-y-4">
                  {Object.entries(workflowData.caseStudies).map(([key, value], index) => (
                    <div key={index} className="bg-white p-4 rounded-md border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2">{String(key)}</h4>
                      <p className="text-gray-700">
                        {typeof value === 'object' ? String(JSON.stringify(value, null, 2)) : String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  {String(JSON.stringify(workflowData.caseStudies))}
                </p>
              )}
            </div>
          </div>

          {/* Recommendations Section */}
          <div className="bg-green-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
              Story Angle Recommendations
            </h3>
            <div className="prose max-w-none">
              {typeof workflowData.recommendations === 'string' ? (
                <div className="space-y-4">
                  {/* Try to parse string recommendations into structured format */}
                  {(() => {
                    const recText = workflowData.recommendations;
                    // Try to split by common patterns
                    const sections = recText.split(/(?:\d+\.|Angle|Recommendation)\s*/i).filter(s => s.trim());
                    if (sections.length > 1) {
                      return sections.slice(1).map((section, index) => (
                        <div key={index} className="bg-white p-4 rounded-md border border-green-200">
                          <h4 className="font-semibold text-green-800 mb-2">
                            {String(`Recommendation ${index + 1}`)}
                          </h4>
                          <p className="text-gray-700 leading-relaxed">
                            {String(section.trim())}
                          </p>
                        </div>
                      ));
                    } else {
                      // Fallback to showing as single text block
                      return (
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {String(recText)}
                        </p>
                      );
                    }
                  })()}
                </div>
              ) : Array.isArray(workflowData.recommendations) ? (
                <div className="space-y-4">
                  {workflowData.recommendations.map((rec, index) => (
                    <div key={index} className="bg-white p-4 rounded-md border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">
                        {String(rec.angle || rec.title || `Recommendation ${index + 1}`)}
                      </h4>
                      <p className="text-gray-700">
                        {String(rec.description || rec.content || rec)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : workflowData.recommendations && typeof workflowData.recommendations === 'object' ? (
                <div className="space-y-4">
                  {Object.entries(workflowData.recommendations).map(([key, value], index) => (
                    <div key={index} className="bg-white p-4 rounded-md border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">{String(key)}</h4>
                      <p className="text-gray-700">
                        {typeof value === 'object' ? String(JSON.stringify(value, null, 2)) : String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  {String(JSON.stringify(workflowData.recommendations))}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {generationStatus && (
        <div className={`p-4 rounded-md ${
          generationStatus.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            {generationStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <p className={`ml-3 text-sm ${
              generationStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {generationStatus.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 3: Select Angles (Multiple)
const Step3SelectAngles = ({ workflowData, setWorkflowData }) => {
  const [selectedAngles, setSelectedAngles] = useState([]);

  // Parse recommendations to extract individual angles with better formatting
  const parseRecommendations = (recommendations) => {
    console.log('🔍 Parsing recommendations:', recommendations);
    console.log('🔍 Type:', typeof recommendations);
    
    if (typeof recommendations === 'string') {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(recommendations);
        if (Array.isArray(parsed)) {
          return parsed.map((rec, index) => ({
            id: rec.id || index + 1,
            title: rec.title || `Video Angle ${index + 1}`,
            description: rec.description || rec.content || String(rec),
            fullText: rec.description || rec.content || String(rec)
          }));
        }
      } catch (e) {
        console.log('Not valid JSON, trying text parsing...');
      }
      
      // Try to extract angles from structured text format
      const angleMatches = recommendations.match(/(?:Angle|Recommendation)\s*\d*[:-]?\s*([^0-9\n]+)/gi);
      if (angleMatches && angleMatches.length > 0) {
        return angleMatches.map((match, index) => {
          const cleanDescription = match.replace(/(?:Angle|Recommendation)\s*\d*[:-]?\s*/i, '').trim();
          return {
            id: index + 1,
            title: `Video Angle ${index + 1}`,
            description: cleanDescription,
            fullText: cleanDescription
          };
        });
      }
      
      // If no structured format, try to split by common patterns
      const sections = recommendations.split(/(?:Angle|Recommendation)\s*\d*[:-]/i).filter(s => s.trim());
      if (sections.length > 1) {
        return sections.slice(1).map((section, index) => ({
          id: index + 1,
          title: `Video Angle ${index + 1}`,
          description: section.trim(),
          fullText: section.trim()
        }));
      }
      
      // If it's just a long string, try to split by sentences or paragraphs
      if (recommendations.length > 100) {
        const sentences = recommendations.split(/[.!?]+/).filter(s => s.trim().length > 20);
        if (sentences.length >= 3) {
          return sentences.slice(0, 3).map((sentence, index) => ({
            id: index + 1,
            title: `Video Angle ${index + 1}`,
            description: sentence.trim(),
            fullText: sentence.trim()
          }));
        }
      }
      
      // Last resort: return the string as a single angle
      return [{
        id: 1,
        title: "AI Generated Angle",
        description: recommendations,
        fullText: recommendations
      }];
    } else if (Array.isArray(recommendations)) {
      return recommendations.map((rec, index) => {
        // Handle different possible structures
        let title = rec.angle || rec.title || `Video Angle ${index + 1}`;
        let description = rec.description || rec.content || String(rec);
        
        // If rec is a string, use it as description
        if (typeof rec === 'string') {
          description = rec;
          title = `Video Angle ${index + 1}`;
        }
        
        return {
          id: index + 1,
          title: String(title),
          description: String(description),
          fullText: String(description)
        };
      });
    } else if (typeof recommendations === 'object' && recommendations !== null) {
      // Handle object format like {"Angle 1": "description", "Angle 2": "description"}
      return Object.entries(recommendations).map(([key, value], index) => ({
        id: index + 1,
        title: key,
        description: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value),
        fullText: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
      }));
    }
    
    console.log('❌ No recommendations parsed, returning empty array');
    return [];
  };

  // Safely parse recommendations with error handling
  let availableAngles = [];
  try {
    availableAngles = parseRecommendations(workflowData.recommendations);
  } catch (error) {
    console.error('Error parsing recommendations:', error);
    console.log('Raw recommendations that caused error:', workflowData.recommendations);
    availableAngles = [];
  }
  
  // Debug logging to see what we're getting
  console.log('Raw recommendations:', workflowData.recommendations);
  console.log('Raw recommendations type:', typeof workflowData.recommendations);
  console.log('Raw recommendations JSON:', JSON.stringify(workflowData.recommendations, null, 2));
  console.log('Parsed angles:', availableAngles);
  
  // Show debug information if no angles are available
  if (!availableAngles || availableAngles.length === 0) {
    console.warn('No angles parsed from recommendations');
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Video Angles Available</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            No video angles were generated from your PDF. Please go back to Step 2 and try generating the summary again.
          </p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-yellow-800 mb-2">Debug Information</h4>
          <div className="text-sm text-yellow-700 space-y-2">
            <p><strong>Recommendations Type:</strong> {typeof workflowData.recommendations}</p>
            <p><strong>Recommendations Value:</strong></p>
            <pre className="bg-yellow-100 p-3 rounded text-xs overflow-auto max-h-96">
              {JSON.stringify(workflowData.recommendations, null, 2)}
            </pre>
            <p><strong>Parsed Angles Count:</strong> {availableAngles.length}</p>
            <p><strong>Parsed Angles:</strong></p>
            <pre className="bg-yellow-100 p-3 rounded text-xs overflow-auto max-h-96">
              {JSON.stringify(availableAngles, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  const handleAngleSelect = (angle) => {
    const isSelected = selectedAngles.some(selected => selected.id === angle.id);
    let newSelectedAngles;
    
    if (isSelected) {
      // Remove angle if already selected
      newSelectedAngles = selectedAngles.filter(selected => selected.id !== angle.id);
    } else {
      // Add angle if not selected (max 3)
      if (selectedAngles.length < 3) {
        newSelectedAngles = [...selectedAngles, angle];
      } else {
        return; // Don't add if already at max
      }
    }
    
    setSelectedAngles(newSelectedAngles);
    setWorkflowData(prev => ({
      ...prev,
      selectedAngles: newSelectedAngles,
      step3Completed: newSelectedAngles.length > 0
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <CheckCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">Select Your Story Angles</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Choose 1-3 angles to create multiple videos from your content. Each selected angle will generate its own script, audio, and video.
        </p>
        {selectedAngles.length > 0 && (
          <p className="text-sm text-purple-600 font-medium mt-2">
            {selectedAngles.length} angle{selectedAngles.length > 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {availableAngles.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
          {availableAngles.map((angle, index) => (
            <div
              key={angle.id}
              onClick={() => handleAngleSelect(angle)}
              className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                selectedAngles.some(selected => selected.id === angle.id)
                  ? 'border-primary-500 bg-primary-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {/* Selection indicator */}
              <div className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedAngles.some(selected => selected.id === angle.id)
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'
              }`}>
                {selectedAngles.some(selected => selected.id === angle.id) && (
                  <CheckCircle className="w-4 h-4 text-white" />
                )}
              </div>

              {/* Angle content */}
              <div className="pr-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {String(angle.title || 'Untitled Angle')}
                </h4>
                <div className="text-gray-600 leading-relaxed">
                  <p className="mb-3 font-medium text-gray-700">
                    {String(angle.description || 'No description available')}
                  </p>
                  {angle.fullText && angle.fullText !== angle.description && (
                    <div className="bg-gray-50 p-3 rounded-md border-l-4 border-primary-200">
                      <p className="text-sm text-gray-600">
                        <strong>Video Focus:</strong> {String(angle.fullText)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Selection indicator */}
              <div className="mt-4">
                <div className={`w-full py-2 px-4 rounded-md text-sm font-medium text-center ${
                  selectedAngles.some(selected => selected.id === angle.id)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedAngles.some(selected => selected.id === angle.id) ? 'Selected' : 'Click to Select'}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Angles Available</h4>
          <p className="text-gray-600">
            Please go back to Step 2 and generate recommendations first.
          </p>
        </div>
      )}

      {/* Selected angles summary */}
      {selectedAngles.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start">
            <CheckCircle className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-lg font-semibold text-green-800 mb-2">
                Selected {selectedAngles.length} Angle{selectedAngles.length > 1 ? 's' : ''}:
              </h4>
              <div className="space-y-2">
                {selectedAngles.map((angle, index) => (
                  <div key={angle.id}>
                    <p className="text-green-700 font-medium">{index + 1}. {angle.title}</p>
                    <p className="text-sm text-green-600">{angle.description}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-green-600 mt-3">
                ✅ These angles will be used to generate your video scripts in the next step.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 4: Select CTA
const Step4SelectCTA = ({ workflowData, setWorkflowData }) => {
  const [selectedCTA, setSelectedCTA] = useState(workflowData.selectedCTA || null);
  const [availableCTAs, setAvailableCTAs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load CTAs from settings
  useEffect(() => {
    const loadCTAs = async () => {
      setLoading(true);
      try {
        const response = await fetch('/api/settings');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings.ctas) {
            const ctas = JSON.parse(data.settings.ctas);
            setAvailableCTAs(ctas);
          }
        }
      } catch (error) {
        console.error('Error loading CTAs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCTAs();
  }, []);

  const handleCTASelect = (cta) => {
    setSelectedCTA(cta);
    setWorkflowData(prev => ({
      ...prev,
      selectedCTA: cta,
      step4Completed: true
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <CheckCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">Select Call-to-Action</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Choose a call-to-action that will be added to the end of each script. This will be used for all selected angles.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading CTAs...</p>
        </div>
      ) : availableCTAs.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
          {availableCTAs.map((cta, index) => (
            <div
              key={index}
              onClick={() => handleCTASelect(cta)}
              className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                selectedCTA === cta
                  ? 'border-primary-500 bg-primary-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {/* Selection indicator */}
              <div className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedCTA === cta
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300'
              }`}>
                {selectedCTA === cta && (
                  <CheckCircle className="w-4 h-4 text-white" />
                )}
              </div>

              {/* CTA content */}
              <div className="pr-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  CTA Option {index + 1}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {cta}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">No CTAs available. Please add some in Settings.</p>
        </div>
      )}

      {selectedCTA && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-green-800 font-medium">Selected CTA:</h4>
              <p className="text-green-700 mt-1">
                {selectedCTA}
              </p>
              <p className="text-sm text-green-600 mt-2">
                ✅ This CTA will be added to all selected angles in the next step.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 5: Generate Scripts
const Step5GenerateScripts = ({ workflowData, setWorkflowData }) => {
  const [generating, setGenerating] = useState(false);
  const [scripts, setScripts] = useState(workflowData.scripts || []);
  const [status, setStatus] = useState(null);
  const [currentScriptIndex, setCurrentScriptIndex] = useState(0);

  const handleGenerateScripts = async () => {
    if (!workflowData.selectedAngles || workflowData.selectedAngles.length === 0) {
      setStatus({ type: 'error', message: 'No angles selected. Please go back to Step 3 and select angles.' });
      return;
    }

    if (!workflowData.selectedCTA) {
      setStatus({ type: 'error', message: 'No CTA selected. Please go back to Step 4 and select a CTA.' });
      return;
    }

    setGenerating(true);
    setStatus(null);
    setScripts([]);
    setCurrentScriptIndex(0);

    try {
      const generatedScripts = [];
      
      // Generate script for each selected angle
      for (let i = 0; i < workflowData.selectedAngles.length; i++) {
        const angle = workflowData.selectedAngles[i];
        setCurrentScriptIndex(i + 1);
        
        // Extract the angle text from the selected angle object
        const angleText = `${angle.title}: ${angle.description}`;
        
        // Generate script with CTA
        const result = await generateV2Script(workflowData.articleId, angleText, workflowData.selectedCTA);
        
        generatedScripts.push({
          id: i + 1,
          angle: angle,
          script: result.script,
          cta: workflowData.selectedCTA
        });
        
        setScripts([...generatedScripts]);
      }
      
      setWorkflowData(prev => ({ 
        ...prev, 
        scripts: generatedScripts,
        step5Completed: true
      }));
      
      setStatus({ type: 'success', message: `Successfully generated ${generatedScripts.length} script${generatedScripts.length > 1 ? 's' : ''}!` });
      
    } catch (error) {
      console.error('Error generating scripts:', error);
      setStatus({ 
        type: 'error', 
        message: `Failed to generate scripts: ${error.message}` 
      });
    } finally {
      setGenerating(false);
      setCurrentScriptIndex(0);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <FileText className="w-16 h-16 text-primary-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">Generate Scripts</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Generate video scripts for each selected angle. Each script will include your chosen call-to-action.
        </p>
        {workflowData.selectedAngles.length > 0 && (
          <p className="text-sm text-purple-600 font-medium mt-2">
            Generating scripts for {workflowData.selectedAngles.length} selected angle{workflowData.selectedAngles.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
      
      {/* Selected Angles Summary */}
      {workflowData.selectedAngles.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Selected Angles</h3>
          <div className="space-y-3">
            {workflowData.selectedAngles.map((angle, index) => (
              <div key={angle.id} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">{angle.title}</h4>
                  <p className="text-sm text-blue-700">{angle.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected CTA Summary */}
      {workflowData.selectedCTA && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">Selected Call-to-Action</h3>
          <p className="text-green-700">{workflowData.selectedCTA}</p>
        </div>
      )}

      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          onClick={handleGenerateScripts}
          disabled={generating || !workflowData.selectedAngles.length || !workflowData.selectedCTA}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
            (generating || !workflowData.selectedAngles.length || !workflowData.selectedCTA) 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
          }`}
        >
          {generating ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
              {currentScriptIndex > 0 ? `Generating Script ${currentScriptIndex}/${workflowData.selectedAngles.length}...` : 'Generating Scripts...'}
            </>
          ) : (
            <>
              <FileText className="-ml-1 mr-3 h-5 w-5" />
              Generate Scripts
            </>
          )}
        </button>
      </div>

      {/* Status Message */}
      {status && (
        <div className={`p-4 rounded-md ${
          status.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {status.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {status.message}
          </div>
        </div>
      )}

      {/* Generated Scripts */}
      {scripts.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Generated Scripts</h3>
          {scripts.map((scriptData, index) => (
            <div key={scriptData.id} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  Script {index + 1}: {scriptData.angle.title}
                </h4>
                <button
                  onClick={() => navigator.clipboard.writeText(scriptData.script)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Script
                </button>
              </div>
              
              <div className="prose max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                  {scriptData.script}
                </pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Step 6: Generate Audio
const Step6GenerateAudio = ({ workflowData, setWorkflowData }) => {
  const [generating, setGenerating] = useState(false);
  const [audioTracks, setAudioTracks] = useState(workflowData.audioTracks || []);
  const [status, setStatus] = useState(null);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);

  const handleGenerateAudio = async () => {
    if (!workflowData.scripts || workflowData.scripts.length === 0) {
      setStatus({ type: 'error', message: 'No scripts available. Please go back to Step 5 and generate scripts.' });
      return;
    }

    setGenerating(true);
    setStatus(null);
    setAudioTracks([]);
    setCurrentAudioIndex(0);

    try {
      const generatedAudioTracks = [];
      
      // Generate audio for each script
      for (let i = 0; i < workflowData.scripts.length; i++) {
        const scriptData = workflowData.scripts[i];
        setCurrentAudioIndex(i + 1);
        
        // Generate audio using ElevenLabs API service
        const result = await generateAudio(
          workflowData.articleId,
          'short_script_text',
          scriptData.script
        );
        
        if (result.file_url) {
          generatedAudioTracks.push({
            id: result.id,
            scriptId: scriptData.id,
            script: scriptData.script,
            angle: scriptData.angle,
            cta: scriptData.cta,
            file_url: result.file_url,
            audio_url: result.file_url
          });
          
          setAudioTracks([...generatedAudioTracks]);
        } else {
          throw new Error(`No audio URL returned for script ${i + 1}`);
        }
      }
      
      setWorkflowData(prev => ({ 
        ...prev, 
        audioTracks: generatedAudioTracks,
        step6Completed: true
      }));
      
      setStatus({ type: 'success', message: `Successfully generated ${generatedAudioTracks.length} audio track${generatedAudioTracks.length > 1 ? 's' : ''}!` });
      
    } catch (error) {
      console.error('Error generating audio:', error);
      setStatus({ 
        type: 'error', 
        message: `Failed to generate audio: ${error.message}` 
      });
    } finally {
      setGenerating(false);
      setCurrentAudioIndex(0);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <PlayCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">Generate Audio</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Convert your scripts to audio using ElevenLabs. Each script will be converted to a separate audio track.
        </p>
        {workflowData.scripts.length > 0 && (
          <p className="text-sm text-purple-600 font-medium mt-2">
            Generating audio for {workflowData.scripts.length} script{workflowData.scripts.length > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Scripts Summary */}
      {workflowData.scripts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Scripts to Convert</h3>
          <div className="space-y-3">
            {workflowData.scripts.map((scriptData, index) => (
              <div key={scriptData.id} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">{scriptData.angle.title}</h4>
                  <p className="text-sm text-blue-700">{scriptData.script.substring(0, 100)}...</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          onClick={handleGenerateAudio}
          disabled={generating || !workflowData.scripts.length}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
            (generating || !workflowData.scripts.length) 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
          }`}
        >
          {generating ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
              {currentAudioIndex > 0 ? `Generating Audio ${currentAudioIndex}/${workflowData.scripts.length}...` : 'Generating Audio...'}
            </>
          ) : (
            <>
              <PlayCircle className="-ml-1 mr-3 h-5 w-5" />
              Generate Audio
            </>
          )}
        </button>
      </div>

      {/* Status Message */}
      {status && (
        <div className={`p-4 rounded-md ${
          status.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {status.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {status.message}
          </div>
        </div>
      )}

      {/* Generated Audio Tracks */}
      {audioTracks.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Generated Audio Tracks</h3>
          {audioTracks.map((audioTrack, index) => (
            <div key={audioTrack.id} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  Audio {index + 1}: {audioTrack.angle.title}
                </h4>
                <a
                  href={audioTrack.audio_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                >
                  🎵 Play Audio
                </a>
              </div>
              
              <div className="text-sm text-gray-600">
                <p><strong>Script:</strong> {audioTrack.script.substring(0, 200)}...</p>
                <p><strong>CTA:</strong> {audioTrack.cta}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Step 7: Generate Videos
const Step7GenerateVideos = ({ workflowData, setWorkflowData }) => {
  const [generating, setGenerating] = useState(false);
  const [videoTracks, setVideoTracks] = useState(workflowData.videoTracks || []);
  const [status, setStatus] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoFormat, setVideoFormat] = useState('16:9');

  const handleGenerateVideos = async () => {
    if (!workflowData.audioTracks || workflowData.audioTracks.length === 0) {
      setStatus({ type: 'error', message: 'No audio tracks available. Please go back to Step 6 and generate audio.' });
      return;
    }

    setGenerating(true);
    setStatus(null);
    setVideoTracks([]);
    setCurrentVideoIndex(0);

    try {
      const generatedVideoTracks = [];
      
      // Generate video for each audio track
      for (let i = 0; i < workflowData.audioTracks.length; i++) {
        const audioTrack = workflowData.audioTracks[i];
        setCurrentVideoIndex(i + 1);
        
        // Generate video using HeyGen
        const response = await generateVideo(
          workflowData.articleId,
          audioTrack.id,
          '15a74300cb31462e8f467b3832fc4dc4', // Default avatar ID
          videoFormat
        );

        if (response.heygen_id) {
          generatedVideoTracks.push({
            id: response.id,
            audioTrackId: audioTrack.id,
            audioTrack: audioTrack,
            heygen_id: response.heygen_id,
            status: response.status,
            format: videoFormat
          });
          
          setVideoTracks([...generatedVideoTracks]);
        } else {
          throw new Error(`No video ID returned for audio track ${i + 1}`);
        }
      }
      
      setWorkflowData(prev => ({ 
        ...prev, 
        videoTracks: generatedVideoTracks,
        step7Completed: true
      }));
      
      setStatus({ type: 'success', message: `Successfully started generation of ${generatedVideoTracks.length} video${generatedVideoTracks.length > 1 ? 's' : ''}! This may take a few minutes.` });
      
    } catch (error) {
      console.error('Error generating videos:', error);
      setStatus({ 
        type: 'error', 
        message: `Failed to generate videos: ${error.message}` 
      });
    } finally {
      setGenerating(false);
      setCurrentVideoIndex(0);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <PlayCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">Generate Videos</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Create avatar videos using your generated audio tracks. Each audio track will be converted to a separate video.
        </p>
        {workflowData.audioTracks.length > 0 && (
          <p className="text-sm text-purple-600 font-medium mt-2">
            Generating videos for {workflowData.audioTracks.length} audio track{workflowData.audioTracks.length > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Audio Tracks Summary */}
      {workflowData.audioTracks.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Audio Tracks to Convert</h3>
          <div className="space-y-3">
            {workflowData.audioTracks.map((audioTrack, index) => (
              <div key={audioTrack.id} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">{audioTrack.angle.title}</h4>
                  <p className="text-sm text-blue-700">Audio Track ID: {audioTrack.id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Avatar Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-purple-800 mb-2">👤 Avatar Selected</h3>
        <p className="text-purple-700">
          Using avatar ID: 15a74300cb31462e8f467b3832fc4dc4
        </p>
      </div>

      {/* Video Format Selection */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📐 Video Format</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <input
              type="radio"
              id="format-16-9"
              name="videoFormat"
              value="16:9"
              checked={videoFormat === '16:9'}
              onChange={(e) => setVideoFormat(e.target.value)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
            />
            <label htmlFor="format-16-9" className="text-sm font-medium text-gray-700">
              16:9 (Landscape) - Perfect for YouTube, LinkedIn, Facebook
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="radio"
              id="format-9-16"
              name="videoFormat"
              value="9:16"
              checked={videoFormat === '9:16'}
              onChange={(e) => setVideoFormat(e.target.value)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
            />
            <label htmlFor="format-9-16" className="text-sm font-medium text-gray-700">
              9:16 (Vertical) - Perfect for TikTok, Instagram Reels, YouTube Shorts
            </label>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          onClick={handleGenerateVideos}
          disabled={generating || !workflowData.audioTracks.length}
          className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
            (generating || !workflowData.audioTracks.length) 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
          }`}
        >
          {generating ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
              {currentVideoIndex > 0 ? `Generating Video ${currentVideoIndex}/${workflowData.audioTracks.length}...` : 'Generating Videos...'}
            </>
          ) : (
            <>
              <PlayCircle className="-ml-1 mr-3 h-5 w-5" />
              Generate Videos
            </>
          )}
        </button>
      </div>
      
      {/* Status Message */}
      {status && (
        <div className={`p-4 rounded-md ${
          status.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {status.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {status.message}
          </div>
        </div>
      )}

      {/* Generated Video Tracks */}
      {videoTracks.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Generated Video Tracks</h3>
          {videoTracks.map((videoTrack, index) => (
            <div key={videoTrack.id} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  Video {index + 1}: {videoTrack.audioTrack.angle.title}
                </h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    videoTrack.status === 'ready' ? 'bg-green-100 text-green-800' :
                    videoTrack.status === 'generating' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {videoTrack.status}
                  </span>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>HeyGen ID:</strong> {videoTrack.heygen_id}</p>
                <p><strong>Format:</strong> {videoTrack.format}</p>
                <p><strong>Audio Track:</strong> {videoTrack.audioTrack.angle.title}</p>
                <p><strong>CTA:</strong> {videoTrack.audioTrack.cta}</p>
              </div>
              
              {videoTrack.status === 'ready' && (
                <div className="mt-4">
                  <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                    🎬 View Video
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default V2Workflow;
